import { Link } from 'react-router-dom';
import { MessageCircle, Mail, Facebook, Instagram, Globe, CalendarDays, Briefcase } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getWhatsAppLink, getEmailLink, EMAIL } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';
import { getSettings } from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_josmart-staging/artifacts/r0wcvy09_file_00000000fee871f598adf429b9fe9c58.png";

export const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    loadSocialLinks();
  }, []);

  const loadSocialLinks = async () => {
    try {
      const data = await getSettings();
      const links = [];
      if (data.facebook_url) links.push({ href: data.facebook_url, label: 'Facebook', icon: 'facebook' });
      if (data.tiktok_url) links.push({ href: data.tiktok_url, label: 'TikTok', icon: 'tiktok' });
      if (data.instagram_url) links.push({ href: data.instagram_url, label: 'Instagram', icon: 'instagram' });
      if (data.snapchat_url) links.push({ href: data.snapchat_url, label: 'Snapchat', icon: 'snapchat' });
      if (data.twitter_url) links.push({ href: data.twitter_url, label: 'Twitter', icon: 'twitter' });
      if (data.linkedin_url) links.push({ href: data.linkedin_url, label: 'LinkedIn', icon: 'linkedin' });
      setSocialLinks(links);
    } catch (err) {
      console.error('Failed to load social links:', err);
    }
  };

  const getSocialIcon = (iconType) => {
    switch (iconType) {
      case 'facebook': return <Facebook size={18} />;
      case 'instagram': return <Instagram size={18} />;
      case 'tiktok':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.88-2.88 2.89 2.89 0 0 1 2.88-2.88c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.75a8.26 8.26 0 0 0 3.76.92V6.22a4.83 4.83 0 0 1 0 .47z"/>
          </svg>
        );
      case 'snapchat':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.501.64.177 1.252.457 1.252.457l-.01.02a1.22 1.22 0 0 1-.5 1.043c-.549.387-1.248.58-1.747.58-.164 0-.311-.015-.443-.043l-.024-.006a8.69 8.69 0 0 1-2.199 3.164 6.56 6.56 0 0 1-2.419 1.556c-.174.073-.314.193-.382.378-.067.187-.041.396.063.56.354.55.58.785.68.904l.036.044c.258.313.283.71.064 1.047a1.17 1.17 0 0 1-.88.47h-.002c-.16.014-.33.02-.5.02-1.104 0-2.097-.396-2.808-.64l-.015-.005a4.42 4.42 0 0 0-.87-.263 2.26 2.26 0 0 0-.377-.031c-.268 0-.473.058-.668.143a3.6 3.6 0 0 1-1.478.323c-.3 0-.614-.043-.93-.128a1.16 1.16 0 0 1-.811-1.122c.004-.16.056-.31.143-.44.097-.142.225-.284.384-.464l.036-.044c.1-.118.326-.354.68-.904.104-.164.13-.373.063-.56-.068-.185-.208-.305-.382-.378a6.56 6.56 0 0 1-2.419-1.556 8.69 8.69 0 0 1-2.199-3.164l-.024.006c-.132.028-.279.043-.443.043-.5 0-1.198-.193-1.747-.58a1.22 1.22 0 0 1-.5-1.043l-.01-.02s.613-.28 1.252-.457a12.72 12.72 0 0 1-.03-.5l-.003-.062c-.104-1.628-.23-3.654.3-4.846C5.447 1.069 8.804.793 9.793.793h2.413z"/>
          </svg>
        );
      case 'twitter':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        );
      case 'linkedin':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        );
      default: return <Globe size={18} />;
    }
  };

  return (
    <footer className="bg-[#0B1521] border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <img src={LOGO_URL} alt="JoSmarTech" className="h-16 w-auto mb-6 brightness-125 rounded-lg" style={{ mixBlendMode: 'screen' }} />
            <p className="text-white/60 text-sm leading-relaxed mb-6">{t('footer_desc')}</p>
            {/* Quick action links */}
            <div className="flex flex-col gap-3">
              <Link to="/rendez-vous" className="flex items-center gap-2 text-[#00CCCC] hover:text-white transition-colors text-sm font-medium" data-testid="footer-rdv-link">
                <CalendarDays size={16} /> {language === 'fr' ? 'Prendre rendez-vous' : 'Book an appointment'}
              </Link>
              <Link to="/jobs" className="flex items-center gap-2 text-[#00CCCC] hover:text-white transition-colors text-sm font-medium" data-testid="footer-jobs-link">
                <Briefcase size={16} /> {language === 'fr' ? 'Rejoindre notre équipe' : 'Join our team'}
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer_contact')}</h4>
            <ul className="space-y-3">
              <li>
                <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/60 hover:text-[#25D366] transition-colors text-sm" data-testid="footer-whatsapp">
                  <MessageCircle size={16} />{t('whatsapp')}
                </a>
              </li>
              <li>
                <a href={getEmailLink()} className="flex items-center gap-2 text-white/60 hover:text-[#4A90D9] transition-colors text-sm" data-testid="footer-email">
                  <Mail size={16} />{EMAIL}
                </a>
              </li>
            </ul>
            {/* Pages */}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/blog" className="text-white/40 hover:text-white text-xs transition-colors" data-testid="footer-blog-link">Blog</Link>
              <Link to="/payment" className="text-white/40 hover:text-white text-xs transition-colors" data-testid="footer-payment-link">{language === 'fr' ? 'Paiement' : 'Payment'}</Link>
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer_social')}</h4>
            <div className="flex flex-wrap gap-3">
              {socialLinks.length > 0 ? (
                socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-[#4A90D9] hover:text-white transition-colors duration-200"
                    data-testid={`footer-social-${social.label.toLowerCase()}`}
                    title={social.label}
                  >
                    {getSocialIcon(social.icon)}
                  </a>
                ))
              ) : (
                <p className="text-white/40 text-sm">{t('footer_social_coming') || 'Bientôt disponible'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">© {currentYear} JoSmarTech. {t('footer_rights')}</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-white/40 hover:text-white text-sm transition-colors">{t('footer_privacy')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
